/**
 * Utility functions for store-related operations
 */

// Default store ID to use for development or when no subdomain is present
const DEFAULT_STORE_ID = '4968f56644a1';

// Cache for store ID to avoid multiple remote calls
let cachedStoreId = null;

/**
 * Fetches store configuration from S3 via CloudFront synchronously
 * Uses a synchronous XMLHttpRequest to avoid async complexity
 * 
 * @param {string} hostname - The hostname to fetch config for
 * @returns {string|null} The store ID from config or null if not found
 */
const fetchStoreConfigSync = (hostname) => {
  try {
    const configUrl = `https://dnzp6dhrl0ccw.cloudfront.net/config/stores/${hostname}.json`;
    const xhr = new XMLHttpRequest();
    xhr.open('GET', configUrl, false); // false = synchronous
    xhr.send();
    
    if (xhr.status === 200) {
      const config = JSON.parse(xhr.responseText);
      return config.storeId || null;
    }
  } catch (error) {
    console.warn(`Failed to fetch store config for ${hostname}:`, error);
  }
  
  return null;
};

/**
 * Extracts the store ID from the current hostname with caching
 * First checks if the domain is in the hardcoded mapping.
 * If the app is running on a subdomain like 8a3d508fc1e3.mydomain.com,
 * it will extract the subdomain as the store ID.
 * If the subdomain pattern fails, it checks S3 for a store config file.
 * Otherwise, it returns the default store ID.
 * 
 * @returns {string} The store ID
 */
export const getStoreId = () => {
  // Return cached result if available
  if (cachedStoreId !== null) {
    return cachedStoreId;
  }
  
  const hostname = window.location.hostname;

  // Check if we're on localhost or an IP address
  if (hostname === 'localhost' || hostname === '127.0.0.1' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
    cachedStoreId = DEFAULT_STORE_ID;
    return cachedStoreId;
  }
  
  // Check if hostname matches STOREID.shopscout.com pattern
  if (hostname.endsWith('.shopscout.com')) {
    const storeIdFromHostname = hostname.replace('.shopscout.com', '');
    // Validate that it looks like a store ID (alphanumeric)
    if (/^[a-zA-Z0-9]+$/.test(storeIdFromHostname)) {
      cachedStoreId = storeIdFromHostname;
      return cachedStoreId;
    }
  }
  
  // Fallback: try to fetch store config from S3
  const storeIdFromConfig = fetchStoreConfigSync(hostname);
  if (storeIdFromConfig) {
    cachedStoreId = storeIdFromConfig;
    return cachedStoreId;
  }
  
  // Final fallback to default
  cachedStoreId = DEFAULT_STORE_ID;
  return cachedStoreId;
};

/**
 * Gets the parquet path for the current store
 * 
 * @returns {string} The parquet path (storeId/products.parquet)
 */
export const getParquetPath = () => {
  return `${getStoreId()}/products.parquet`;
};